/** @jsx jsx */
import * as React from "react";
import { isEmpty as _isEmpty } from "lodash";
import { ITimeInterval } from "../../models/Roster/ITimeInterval";
import { ModalComponent } from "../ModalComponent";
import { AppConstants, CALENDAR_DAYS, DEFAULT_WORKING_HOURS_ACTIONS } from "../../utils/AppConstants";
import * as moment from "moment";;
import IDoctorProfile from "../../models/IDoctorProfile";
import { uniq as _uniq } from "lodash";
import { Tracker } from "../../utils/Tracker";
import { EventNames } from "../../utils/EventTrackerConstants";
import LocalStorageUtils from "../../utils/LocalStorageUtils";
import AppUtils from "../../utils/AppUtils";
import { rosterStyles } from "../../styles/roster/mobile/roster";
import { css, jsx } from "@emotion/core";
import { default as CTA, CTATheme } from "../common/CallToAction";
import { RosterCalendarStyles, rosterInnerContainer, rosterButtonStyles } from "../../styles/components/Roster/RosterCalendar";
import { IUpdateDefaultWorkingHoursRequest } from "../../models/defaultWorkingHours/IDefaultWorkingHoursRequest";
import { DefaultWorkingConfirmationModal } from "./ConfirmationModal";

interface ICopyRosterModalProps {
    shift: ITimeInterval;
    closeCopyModal(): void;
    datesAvailableToCopy: string[];
    selectedDoctor: IDoctorProfile;
    copyShifts(addRosterParams: IUpdateDefaultWorkingHoursRequest[], userAction?: DEFAULT_WORKING_HOURS_ACTIONS): void;
    allDaysData?: any;
    dateCurrentlyCopying?: string;
    title?: string;
}

interface ICopyRosterModalState {
    selectedDatesToCopyTo: string[];
    errMsg: string;
    conflictingShifts: IUpdateDefaultWorkingHoursRequest[];
    overrideConflicts: boolean;
    showUserInputModal: boolean;
    showConfirmationModal: boolean;
    effectiveDate: string;
}

export class CopyDefaultWorkingHoursModal extends React.Component<ICopyRosterModalProps, ICopyRosterModalState> {
    constructor(props: ICopyRosterModalProps) {
        super(props);
        this.state = {
            selectedDatesToCopyTo: [],
            errMsg: null,
            conflictingShifts: null,
            overrideConflicts: false,
            showConfirmationModal: false,
            showUserInputModal: false,
            effectiveDate: null
        };
        this.selectDate = this.selectDate.bind(this);
        this.updateCopiedShifts = this.updateCopiedShifts.bind(this);
        this.preventModalClose = this.preventModalClose.bind(this);
    }

    componentDidMount() {
        this.setState({ showUserInputModal: true });
        Tracker.track(EventNames.Copy_Automated_Working_Hours_Opened);
    }

    private preventModalClose(e: React.MouseEvent<HTMLDivElement>): void {
        e.preventDefault();
        e.stopPropagation();
    }

    private selectDate(date: string): void {
        const { selectedDatesToCopyTo } = this.state;
        const index = selectedDatesToCopyTo.findIndex((selectDate) => selectDate === date);
        if (index === -1) {
            LocalStorageUtils.setClickEvents({ field: "Select Day", value: date, selected: true, widget: EventNames.Copy_Automated_Working_Hours, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
            selectedDatesToCopyTo.push(date);
        } else {
            LocalStorageUtils.setClickEvents({ field: "Select Day", value: date, selected: false, widget: EventNames.Copy_Automated_Working_Hours, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
            selectedDatesToCopyTo.splice(index, 1);
        }
        this.setState({ selectedDatesToCopyTo });
    }


    private updateCopiedShifts(e: React.MouseEvent<HTMLButtonElement>): void {
        const { overrideConflicts } = this.state;

        LocalStorageUtils.setClickEvents({ field: EventNames.Save, widget: EventNames.Copy_Automated_Working_Hours, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
        Tracker.track(EventNames.Copy_Modal_Save_Button)
        this.setState({ errMsg: null });
        e.stopPropagation();
        e.preventDefault();

        const conflictingShifts = this.checkConflictingShifts(this.props.shift, this.state.selectedDatesToCopyTo);

        if (conflictingShifts.length && !overrideConflicts) {
            const conflictingDates = _uniq(conflictingShifts.map(({ dayOfWeek }) => dayOfWeek)).map(day => Object.keys(CALENDAR_DAYS).find(key => CALENDAR_DAYS[key].value === day))
            this.setState({ errMsg: `${AppConstants.ROSTER_MESSAGES.CONFLICTING_ROSTER}${conflictingDates.join(", ")}.`, conflictingShifts });
        } else {
            const effectiveDate = AppUtils.getDateFromNumberOfDays();
            this.setState({ showConfirmationModal: true, showUserInputModal: false, effectiveDate });
        }
    }

    checkConflictingShifts = (shiftToCheck: ITimeInterval, daysToCheckOn: string[]) => {
        const { allDaysData } = this.props;
        let currentPresentIntervalsForGivenDates: any[] = [];
        daysToCheckOn.forEach(day => currentPresentIntervalsForGivenDates = currentPresentIntervalsForGivenDates.concat(allDaysData[day]));

        const conflictingShifts = currentPresentIntervalsForGivenDates.filter(({ startTime, endTime }) => {
            const workingDayDate = moment(new Date()).valueOf();
            const startEpochOfExisitingShift = AppUtils.getEpochFromDateAndTime(workingDayDate, startTime);
            const endEpochOfExisitingShift = AppUtils.getEpochFromDateAndTime(workingDayDate, endTime);
            const startEpochOfShiftToCheck = AppUtils.getEpochFromDateAndTime(workingDayDate, shiftToCheck.startTime);
            const endEpochOfShiftToCheck = AppUtils.getEpochFromDateAndTime(workingDayDate, shiftToCheck.endTime);

            return moment(startEpochOfShiftToCheck).isBefore(moment(endEpochOfExisitingShift)) && moment(endEpochOfShiftToCheck).isAfter(moment(startEpochOfExisitingShift));
        });
        return conflictingShifts;
    }

    closeModal = () => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Cancel, widget: EventNames.Copy_Automated_Working_Hours, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
        Tracker.track(EventNames.Copy_Modal_Cancel_Button)
        this.props.closeCopyModal()
    }

    editExistingShifts = () => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Go_Back_To_Change_Selection, widget: EventNames.Automated_Working_Hours_Confirmation_Modal, flow: EventNames.Copy_Automated_Working_Hours, section: EventNames.Automated_Working_Hours_Confirmation_Modal });
        Tracker.track(EventNames.Go_Back_To_Change_Selection);
        this.setState({ showConfirmationModal: false, showUserInputModal: true });
    }

    saveShifts = () => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Proceed_with_Selection, widget: EventNames.Automated_Working_Hours_Confirmation_Modal, flow: EventNames.Copy_Automated_Working_Hours, section: EventNames.Automated_Working_Hours_Confirmation_Modal });
        Tracker.track(EventNames.Proceed_with_Selection);
        const { selectedDatesToCopyTo, overrideConflicts, conflictingShifts } = this.state;
        const { selectedDoctor, shift: { startTime, endTime } } = this.props;
        const addRosterParams: IUpdateDefaultWorkingHoursRequest[] = selectedDatesToCopyTo.map((day: string) => {
            return {
                agentShiftDeallocationMapping: [],
                startTime,
                endTime,
                agentId: selectedDoctor.id,
                centerId: selectedDoctor.agentCenterMapping && selectedDoctor.agentCenterMapping[0] && selectedDoctor.agentCenterMapping[0].centerId,
                dayOfWeek: day,
                shiftName: day,
                isActive: true
            }
        });
        if (overrideConflicts) {
            conflictingShifts.forEach(shift => {
                shift.isActive = false;
                addRosterParams.push(shift);
            })
        }
        this.setState({ conflictingShifts: null, overrideConflicts: false });
        this.props.closeCopyModal()
        this.props.copyShifts(addRosterParams, DEFAULT_WORKING_HOURS_ACTIONS.COPY);
    }

    overrideConflicts = () => {
        const { overrideConflicts } = this.state
        LocalStorageUtils.setClickEvents({ field: "Override Conflicts", widget: EventNames.Copy_Automated_Working_Hours, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
        this.setState({ overrideConflicts: !overrideConflicts })
    }

    render() {
        const { closeCopyModal, datesAvailableToCopy, shift, selectedDoctor, title, dateCurrentlyCopying } = this.props;
        const { selectedDatesToCopyTo, errMsg, conflictingShifts, overrideConflicts, showConfirmationModal, showUserInputModal, effectiveDate } = this.state;
        const isMobile: boolean = AppUtils.getIsMobile();
        const agentCentreMissing = _isEmpty(selectedDoctor.agentCenterMapping);
        const agentMappedWithMultipleCentres = !agentCentreMissing && selectedDoctor.agentCenterMapping.length > 1;
        return (
            <div className="roster-modal-container">
                {showUserInputModal &&
                    <ModalComponent modalSize={"small"} onClickCloseHandler={closeCopyModal}>
                        <div onClick={this.preventModalClose}>
                            <div className="roster-modal-header">
                                <h3>{title}</h3>
                                <div onClick={closeCopyModal}>x</div>
                            </div>
                            <hr></hr>
                            <div className="roster-modal-body">
                                <h3>{Object.keys(CALENDAR_DAYS).find(key => CALENDAR_DAYS[key].value === dateCurrentlyCopying)}</h3>
                                <table css={isMobile ? rosterStyles.copyTable : {}}>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <label>START TIME</label>
                                                <div className="date-copy-text">{AppUtils.get12HourFormatFrom24Hour(shift.startTime)}</div>
                                            </td>
                                            <td>
                                                <label>END TIME</label>
                                                <div className="date-copy-text">{AppUtils.get12HourFormatFrom24Hour(shift.endTime)}</div>
                                            </td>
                                        </tr>
                                        <tr className="spacer"></tr>
                                    </tbody>
                                </table>
                                <div css={isMobile ? rosterStyles.daysList : {}} className="days-list">
                                    <React.Fragment>
                                        {
                                            isMobile &&
                                            datesAvailableToCopy.map((date: string) => <button onClick={() => this.selectDate(date)}
                                                className={selectedDatesToCopyTo.includes(date) ? "selected date-btn-mob" : "date-btn-mob"} key={date}>
                                                {Object.keys(CALENDAR_DAYS).find(key => CALENDAR_DAYS[key].value === date)}
                                            </button>)
                                        }
                                        {
                                            !isMobile &&
                                            <div css={RosterCalendarStyles.copyRosterWrapper}>
                                                {
                                                    datesAvailableToCopy.map((day: any, index: number) => {
                                                        let shouldDisable = day === dateCurrentlyCopying;
                                                        return (
                                                            <div className={rosterInnerContainer} key={`${day}-${index}`}>
                                                                <div css={RosterCalendarStyles.rosterCalendarLabel} className={shouldDisable ? "date-btn disable-day" : selectedDatesToCopyTo.includes(day) ? "selected date-btn" : "date-btn"} onClick={() => this.selectDate(day)}>{Object.keys(CALENDAR_DAYS).find(key => CALENDAR_DAYS[key].value === day)}</div>
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>
                                        }
                                    </React.Fragment>
                                </div>
                                {errMsg &&
                                    <div className="warning top-space">{errMsg}</div>
                                }
                                {/* </div> */}


                                {agentCentreMissing &&
                                    <div className="warning">
                                        <div>* Cannot create your roster.</div>
                                        <div>You are currently not assigned to any center. Please contact Care.Fit team.</div>
                                    </div>
                                }
                                {agentMappedWithMultipleCentres &&
                                    <div className="warning">
                                        <div>* Cannot create your roster.</div>
                                        <div>You are currently assigned to multiple centers. Please contact Care.Fit team.</div>

                                    </div>
                                }
                                {
                                    conflictingShifts && conflictingShifts.length > 0 &&
                                    <div className="warning" onClick={this.overrideConflicts}>
                                        {overrideConflicts &&
                                            <React.Fragment>
                                                <input className="override-checkbox" type="checkbox" checked />Delete conflicting shifts and create roster ?
                                        </React.Fragment>

                                        }
                                        {!overrideConflicts &&
                                            <React.Fragment>
                                                <input className="override-checkbox" type="checkbox" />Delete conflicting shifts and create roster ?
                                        </React.Fragment>

                                        }
                                    </div>
                                }
                                {/* </React.Fragment> */}
                            </div>

                            <hr></hr>
                            {selectedDoctor &&
                                <div className="roster-modal-footer">
                                    <CTA innerText="Cancel" theme={CTATheme.LIGHT} onClick={this.closeModal} customStyle={rosterButtonStyles} />
                                    <CTA innerText="Save" theme={CTATheme.PRIMARY} onClick={this.updateCopiedShifts} disabled={selectedDoctor.agentCenterMapping.length !== 1} customStyle={rosterButtonStyles} />
                                </div>
                            }
                        </div>
                    </ModalComponent >
                }
                {
                    showConfirmationModal &&
                    <DefaultWorkingConfirmationModal editShifts={this.editExistingShifts} saveShifts={this.saveShifts} effectiveDate={effectiveDate}></DefaultWorkingConfirmationModal>
                }
            </div >
        )
    }
}